import type { TowerDesign } from "@/game/towerSpec";
import type { Material } from "@/physics/model";
import { CATALOG } from "@/game/appurtenances";

/**
 * Norwalk 150 ft monopole — a real Valmont pole at 600 Connecticut Ave, Norwalk,
 * Fairfield County CT (Crown Castle BU 841287, AT&T co-locate). Rebuilt from the
 * stamped tnxTower structural analysis (Rev 3, 2019-06-28) in
 * reference_towers/02_engineering_drawings/CT_Norwalk_Valmont_150ft_monopole_structural.pdf.
 *
 * Real structure (from the tnxTower "Tapered Pole Section Geometry", pp.8-10):
 *   - 12-sided tapered tube, 39 shaft sections over 150 ft (45.72 m).
 *   - Base OD 36.00" (0.914 m), 0.600" wall; tip OD 14.50" (0.368 m), 0.250" wall.
 *   - Grade A572-65 throughout: Fy 65 ksi (448 MPa), Fu 80 ksi. This is stiffer
 *     than the game's default a572 (Gr50, 345 MPa), so it's given inline below.
 *   - The lower ~140 ft is sleeve-REINFORCED (Reinf. 1-5); "Reinf. Tension
 *     Rupture" governs the pole rating at 86.6%, overall structure 95.5%.
 *
 * Design basis (pp.3, Tower Design Notes p.E-1): TIA-222-H, Risk Cat II, 120 mph
 * ultimate 3-sec gust, Exposure B, Topo Cat 1; also 50 mph + 1.50" ice; service
 * 60 mph. Factored base reactions (120 mph): axial 33 kip (147 kN), shear 23 kip
 * (102 kN), overturning moment 2258 kip-ft (3.06 MN·m).
 *
 * Foundation & connection (Table 4: base plate 59.1%, anchor rods 71.4%, pier
 * 58.9%, soil 56.1% — all Pass, none governing): a drilled pier with an
 * anchor-bolt base plate. Geometry per the FDH Engineering geotech/foundation set
 * and the documented Norwalk detail cited in physics/baseConnection.ts:
 *   - (8) 1-3/4" F1554 Gr.105 anchor rods on a 49" (1.245 m) bolt circle.
 *   - 30-bar Grade 60 pier reinforcement cage.
 *   - dense CT glacial till → dense-sand soil preset; ~2.7 m dia × 7.6 m drilled
 *     pier, 30 MPa concrete. Sized so (as in the report) the ground and joint are
 *     NOT the governing failure — the pole is.
 *
 * Game modelling note (see docs/TOWER_SPEC.md §7): a monopole is a moment tube,
 * but the pin-jointed sim represents it as a slim braced lattice clad in a `shell`
 * tube. A 45.7 m pin-lattice needs real width for lateral (P-delta) stiffness, so
 * the internal 12-leg cage is widened to 1.2 m across (baseRadius 0.6) — wider
 * than the true 0.914 m pole — and the shell encloses it. Faces = 12 to match the
 * real 12-sided section; heavy 8" pipe legs stand in for the thick tube wall.
 *
 * Appurtenances: the maxed 3-sector array at 152 ft (46.3 m) from Table 1 — 6
 * Andrew SBNHH panels + 3 Quintel panels, 15 Ericsson RRUS radios, 6 Powerwave
 * 7020 + 6 LGP21401 + 3 7770 (modelled as small panels/radios), and the feed-line
 * bundle (12×1-5/8" + 6×3/4" + 4×3/8"), across three azimuths.
 */

// Real Valmont pole steel: ASTM A572 Grade 65 (Fy 65 ksi ≈ 448 MPa). [p.E-1]
const A572_65: Material = { name: "A572 Gr65", E: 200e9, Fy: 448e6, rho: 7850 };

// The three sector azimuths (A/B/C ≈ 0°/120°/240°), radians.
const AZ = [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3];
const H = 46.3; // main antenna centreline, 152 ft

export const design: TowerDesign = {
  id: "monopole-norwalk-150ft",
  name: "Monopole (Norwalk 150 ft, Valmont)",
  description: "Real 150 ft Valmont A572-65 monopole, Norwalk CT (Crown Castle BU 841287).",
  author: "reference: CT Norwalk Valmont 150 ft tnxTower SA Rev 3",
  spec: {
    type: "monopole",
    height: 45.72, // 150 ft
    stack: {
      faces: 12, // matches the real 12-sided pole section
      panels: 30, // 5 ft cans, matching the real 39-section schedule granularity
      baseRadius: 0.4572, // EXACT real base 36.000" OD / 2 (slim → the pin-lattice may sway; acceptable)
      topRadius: 0.1842, // EXACT real tip 14.500" OD / 2
      legSection: "legXXL", // internal lattice stand-in for the thick tube wall
      braceSection: "braceHeavy",
      legMaterial: A572_65,
      braceMaterial: A572_65,
      bracing: "X",
    },
    // EXACT per-section pole geometry (tnxTower "Tapered Pole Section Geometry",
    // 12-sided, all A572-65). OD bottom→top, wall (in): the tube tapers 36.000"
    // base → 14.500" tip over 39 cans; wall 0.600" base → 0.250" tip; the lower
    // ~140 ft carries an internal reinforcement sleeve (Reinf. 1-5, the small OD
    // steps at 66/51/26 ft). Shell uses the real end ODs:
    shell: {
      baseDiameter: 0.9144, // real 36.000" base OD
      topDiameter: 0.3683, // real 14.500" tip OD
    },
    foundation: {
      soil: "dense-sand", // dense Fairfield County glacial till (FDH geotech)
      base: {
        type: "pier", // drilled shaft — monopole pier
        width: 2.7, // (unused for pier; sliding/overturning use diameter)
        diameter: 2.7, // ~9 ft drilled pier
        depth: 7.6, // ~25 ft embedment
        concrete: "c30", // 30 MPa drilled-shaft mix
      },
      connection: {
        kind: "anchorBolts",
        nBolts: 8, // (8) 1-3/4" anchor rods [Norwalk detail]
        bolt: "b1_75",
        grade: "f1554_105", // F1554 Gr.105 (Fy 105 ksi, Fu 125 ksi)
        boltCircleDia: 1.245, // 49" bolt circle
        embedment: 0.43, // ~17" effective embedment
        edgeDistance: 0.3,
        platePlanArea: 1.9, // ~55" square base plate
        plateThickness: 0.057, // ~2.25"
        plateGrade: "a36rod", // A36 base plate
        fc: 30e6,
      },
    },
    loads: [
      // 6 Andrew SBNHH + 3 Quintel = 9 sector panels (3 per azimuth)
      ...AZ.map((azimuth) => ({ appurtenance: CATALOG.panelSector, height: H, azimuth })),
      ...AZ.map((azimuth) => ({ appurtenance: CATALOG.panelSector, height: H, azimuth })),
      ...AZ.map((azimuth) => ({ appurtenance: CATALOG.panelSector, height: H, azimuth })),
      // 15 Ericsson RRUS radios (5 per azimuth)
      ...AZ.flatMap((azimuth) =>
        [0, 0, 0, 0, 0].map(() => ({ appurtenance: CATALOG.rru, height: H, azimuth })),
      ),
      // Powerwave 7020 / LGP21401 / 7770 → small panels/radios (3 per azimuth)
      ...AZ.map((azimuth) => ({ appurtenance: CATALOG.panelSmall, height: H, azimuth })),
      ...AZ.map((azimuth) => ({ appurtenance: CATALOG.panelSmall, height: H, azimuth })),
      ...AZ.map((azimuth) => ({ appurtenance: CATALOG.panelSmall, height: H, azimuth })),
      // feed-line bundle (12×1-5/8" + 6×3/4" + 4×3/8"), base → array
      { appurtenance: CATALOG.coaxBundle, height: H, runLength: H },
      { appurtenance: CATALOG.coaxBundle, height: H, runLength: H },
    ],
  },
};
